import React, { useEffect, useState } from 'react';
import { api } from '../../api/nocode';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import SafeIcon from '../../common/SafeIcon';

const EMPTY_EPISODE = {
  title: '',
  body: '',
  video_url: '',
  is_active: 0
};

function EpisodeEditor({ number, episode, onChange, onSave, saving }) {
  const update = (field, value) => onChange({ ...episode, [field]: value });
  const sourceLabel = number === 1 ? 'Morning Assembly' : 'Midday Mess Hall';

  return (
    <Card className="space-y-5 border border-tan shadow-sm">
      <div className="flex items-center justify-between border-b border-tan pb-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-dark-green font-bold text-white">
            {number}
          </span>
          <div>
            <h2 className="font-bold text-dark-green">Session {number}</h2>
            <p className="text-xs text-muted">Private podcast episode</p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => update('is_active', episode?.is_active ? 0 : 1)}
          className={`rounded-full px-4 py-2 text-xs font-bold transition ${
            episode?.is_active
              ? 'bg-forest-green text-white'
              : 'bg-gray-100 text-gray-500'
          }`}
        >
          {episode?.is_active ? 'Active' : 'Draft'}
        </button>
      </div>

      <div className="flex items-start gap-3 rounded-md border border-amber/30 bg-amber/10 p-4">
        <SafeIcon name="Link" className="mt-0.5 shrink-0 text-amber" />
        <p className="text-sm leading-relaxed text-dark-green">
          This episode automatically unlocks when the Day {episode?.day} {sourceLabel}{' '}
          session unlocks. Set the release date and time in the Content Manager.
        </p>
      </div>

      <Input
        label="Episode title"
        value={episode?.title ?? ''}
        onChange={(event) => update('title', event.target.value)}
        placeholder={`Day episode ${number} title`}
      />

      <Textarea
        label="Episode description"
        value={episode?.body ?? ''}
        onChange={(event) => update('body', event.target.value)}
        placeholder="Introduce this episode and what campers will learn."
        className="min-h-[120px]"
      />

      <Input
        label="Audio file URL"
        type="url"
        value={episode?.video_url ?? ''}
        onChange={(event) => update('video_url', event.target.value)}
        placeholder="https://example.com/private-episode.mp3"
      />

      <Button
        onClick={onSave}
        disabled={saving}
        className="w-full bg-amber text-white hover:bg-orange-600"
      >
        <SafeIcon name="Save" className="mr-2" />
        {saving ? 'Saving Episode...' : `Save Session ${number}`}
      </Button>
    </Card>
  );
}

function BonusEpisodeEditor({ episode, onChange, onSave, saving }) {
  const update = (field, value) => onChange({ ...episode, [field]: value });

  return (
    <Card className="space-y-5 border border-tan shadow-sm">
      <div className="flex items-center justify-between border-b border-tan pb-4">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-dark-green font-bold text-white">
            +
          </span>
          <div>
            <h2 className="font-bold text-dark-green">Bonus Session</h2>
            <p className="text-xs text-muted">Optional private podcast episode</p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => update('is_active', episode?.is_active ? 0 : 1)}
          className={`rounded-full px-4 py-2 text-xs font-bold transition ${
            episode?.is_active
              ? 'bg-forest-green text-white'
              : 'bg-gray-100 text-gray-500'
          }`}
        >
          {episode?.is_active ? 'Active' : 'Draft'}
        </button>
      </div>

      <div className="flex items-start gap-3 rounded-md border border-amber/30 bg-amber/10 p-4">
        <SafeIcon name="Link" className="mt-0.5 shrink-0 text-amber" />
        <p className="text-sm leading-relaxed text-dark-green">
          This bonus episode automatically unlocks when the Day {episode?.day} Midday Mess Hall
          session unlocks. Set the release date and time in the Content Manager.
        </p>
      </div>

      <Input
        label="Episode title"
        value={episode?.title ?? ''}
        onChange={(event) => update('title', event.target.value)}
        placeholder="Day bonus session title"
      />

      <Textarea
        label="Episode description"
        value={episode?.body ?? ''}
        onChange={(event) => update('body', event.target.value)}
        placeholder="Introduce this bonus episode and what campers will learn."
        className="min-h-[120px]"
      />

      <Input
        label="Audio file URL"
        type="url"
        value={episode?.video_url ?? ''}
        onChange={(event) => update('video_url', event.target.value)}
        placeholder="https://example.com/private-bonus-episode.mp3"
      />

      <Button
        onClick={onSave}
        disabled={saving}
        className="w-full bg-amber text-white hover:bg-orange-600"
      >
        <SafeIcon name="Save" className="mr-2" />
        {saving ? 'Saving Bonus Session...' : 'Save Bonus Session'}
      </Button>
    </Card>
  );
}

export default function PodcastManager() {
  const [activeDay, setActiveDay] = useState(1);
  const [episodes, setEpisodes] = useState([]);
  const [savingKey, setSavingKey] = useState('');
  const [message, setMessage] = useState('');

  const loadEpisodes = async () => {
    try {
      const response = await api.get('camp_content_gwacamp');
      const rows = Array.isArray(response) ? response : response?.data ?? [];

      setEpisodes(
        (rows ?? []).filter((row) =>
          ['podcast_1', 'podcast_2'].includes(row?.block)
        )
      );

      setEpisodes((current) => [
        ...current,
        ...(rows ?? []).filter((row) => row?.block === 'podcast_3')
      ]);
    } catch (error) {
      setMessage(`Unable to load podcast episodes: ${error.message}`);
    }
  };

  useEffect(() => {
    loadEpisodes();
  }, []);

  const getEpisode = (session) => {
    const block = `podcast_${session}`;

    return episodes.find(
      (episode) =>
        Number(episode?.day) === activeDay && episode?.block === block
    ) ?? { ...EMPTY_EPISODE, day: activeDay, block };
  };

  const updateEpisode = (session, value) => {
    const block = `podcast_${session}`;
    const existing = getEpisode(session);
    const nextEpisode = { ...existing, ...value, day: activeDay, block };

    setEpisodes((current) => {
      const index = current.findIndex(
        (item) =>
          Number(item?.day) === activeDay && item?.block === block
      );

      if (index === -1) return [...current, nextEpisode];

      const next = [...current];
      next[index] = nextEpisode;
      return next;
    });
  };

  const saveEpisode = async (session) => {
    const episode = getEpisode(session);
    const key = `${activeDay}-${session}`;

    if (!episode?.title?.trim() || !episode?.video_url?.trim()) {
      setMessage('Add an episode title and audio file URL before saving.');
      return;
    }

    setSavingKey(key);
    setMessage('');

    const payload = {
      day: activeDay,
      block: `podcast_${session}`,
      title: episode.title.trim(),
      body: episode.body ?? '',
      video_url: episode.video_url.trim(),
      is_active: episode.is_active ? 1 : 0,
      owner_id: 'admin'
    };

    try {
      if (episode?.id) {
        await api.put('camp_content_gwacamp', episode.id, payload);
      } else {
        await api.post('camp_content_gwacamp', payload);
      }

      await loadEpisodes();
      setMessage(`Day ${activeDay}, Session ${session} saved successfully.`);
    } catch (error) {
      setMessage(`Unable to save the episode: ${error.message}`);
    } finally {
      setSavingKey('');
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-7 pb-20">
      <header>
        <div className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-amber">
          <SafeIcon name="Headphones" />
          All-Access Content
        </div>
        <h1 className="text-3xl font-bold text-dark-green">Private Podcast</h1>
        <p className="mt-2 max-w-2xl text-muted">
          Publish two private audio sessions per camp day. Session 1 follows
          Morning Assembly, while Session 2 follows Midday Mess Hall.
        </p>
      </header>

      {message && (
        <div className="rounded-md border border-tan bg-white px-5 py-3 text-sm font-medium text-dark-green shadow-sm">
          {message}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {[1, 2, 3, 4, 5].map((day) => (
          <button
            type="button"
            key={day}
            onClick={() => {
              setActiveDay(day);
              setMessage('');
            }}
            className={`rounded-md px-4 py-3 font-bold transition ${
              activeDay === day
                ? 'bg-dark-green text-white shadow-md'
                : 'border border-tan bg-white text-dark-green hover:border-amber'
            }`}
          >
            Day {day}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {[1, 2].map((session) => (
          <React.Fragment key={`podcast-order-${activeDay}-${session}`}>
            {session === 2 && (
              <BonusEpisodeEditor
                episode={getEpisode(3)}
                onChange={(value) => updateEpisode(3, value)}
                onSave={() => saveEpisode(3)}
                saving={savingKey === `${activeDay}-3`}
              />
            )}

            <EpisodeEditor
              key={`${activeDay}-${session}`}
              number={session}
              episode={getEpisode(session)}
              onChange={(value) => updateEpisode(session, value)}
              onSave={() => saveEpisode(session)}
              saving={savingKey === `${activeDay}-${session}`}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}